import { Fragment, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, type ImportacionProductos } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useEstadoGuardado } from '../lib/persistencia';
import { useEsCelular } from '../lib/useMedia';
import { csvDeFilas, filaVacia, filasVacias, tieneAlgo, type ColumnaManual, type FilaManual } from '../lib/csvManual';
import { Cambia,
  Buscador,
  Cargando,
  ChipBoton,
  Encabezado,
  EstadoChip,
  filtrar,
  Icono,
  Kpi,
  Numero,
  Paginador,
  usePaginado,
  PanelError,
  useConfirmar,
  Vacio,
} from '../components/ui';
import { abrir, aparecer, CURVA, desplegar, fila, revelarDesde } from '../components/movimiento';
import {
  altoMinimo,
  aviso,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  encabezadoTabla,
  etiquetaCampo,
  filaTabla,
  miles,
  mono,
  nota,
  tarjeta,
} from '../theme';


/** Un código de producto de verdad trae al menos un dígito (`1721423040`). Si no tiene ninguno, es candidato a que el nombre y el código hayan quedado cargados al revés. Es una pista, no un diagnóstico. */
const pareceCodigoDeMaterial = (codigo: string) => /\d/.test(codigo);

/** Las columnas de la carga a mano de productos; ver `lib/csvManual.ts` por qué es un CSV. */
const COLUMNAS_PRODUCTOS: ColumnaManual[] = [
  { clave: 'numero_de_parte', rotulo: 'N° de parte', ancho: 170, ejemplo: '1721423040' },
  { clave: 'articulo', rotulo: 'Artículo', ancho: 150, ejemplo: 'TUBE' },
  { clave: 'descripcion', rotulo: 'Descripción', ancho: 260, ejemplo: 'OUTER TUBE 65x82x389' },
  { clave: 'proveedor', rotulo: 'Proveedor', ancho: 200, ejemplo: 'ZF Friedrichshafen AG' },
  { clave: 'max_por_capacho', rotulo: 'Piezas por capacho', ancho: 150, ejemplo: '45' },
];

const vaciarProductos = () => filasVacias(COLUMNAS_PRODUCTOS);

/** Una fila de una tabla armada a mano: entra escalonada. Sin salida: se quitan por posición y la clave es el índice. */
const filaNueva = (i: number) => {
  const { initial, animate, transition } = fila(i);
  return { initial, animate, transition };
};

const campoTabla = { ...campo, height: 36, padding: '0 12px', fontSize: 13 };

/**
 * La casilla de marcar de cada fila. El label es lo que se toca: ocupa el alto
 * entero de la fila (el margen negativo se come el padding de `filaTabla`), así
 * con el dedo no hay que acertarle a un cuadradito de 13 px.
 */
const tildeEnFila = {
  width: 40,
  height: 40,
  margin: '-8px 0 -8px -10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
  cursor: 'pointer',
} as const;

/**
 * Las celdas de la tabla del catálogo salen de las mismas medidas que las filas
 * de las otras listas, sin el `display:flex` (una celda no es un contenedor
 * flex) y sin `display` en línea: el celular lo cambia por CSS (`.wms-tarjetas`)
 * y un estilo en línea le ganaría.
 */
const { display: _d1, alignItems: _a1, gap: _g1, ...celda } = filaTabla;
const { display: _d2, alignItems: _a2, gap: _g2, ...celdaTitulo } = encabezadoTabla;
const titulo = { ...celdaTitulo, textAlign: 'left', fontWeight: 600 } as const;

/** Un dato que se edita sin salir de la fila: el alto de la fila, no el del formulario. */
const campoEnFila = {
  width: '100%',
  minWidth: 0,
  flex: 1,
  height: 32,
  padding: '0 10px',
  borderRadius: 8,
  background: c.panel,
  fontSize: 13,
  color: c.texto,
} as const;

/**
 * Catálogo de artículos.
 *
 * Los artículos entran solos con el importador, pero el proveedor no siempre
 * viene en la planilla del cliente y hace falta para la etiqueta del capacho.
 * Acá se completa sin volver a importar el depósito entero.
 *
 * Se puede cargar de a uno o marcando varios y poniéndoles el mismo proveedor,
 * que es lo que pasa en la práctica: llegan tandas del mismo origen.
 *
 * También es la pantalla donde se arregla un material que quedó cargado al
 * revés (el nombre en el código, la descripción larga en el nombre): cada
 * fila se puede desplegar para editar la descripción larga y, sobre todo,
 * para renombrar el código — que es un cambio de clave primaria, así que
 * muestra antes de confirmar cuánto stock arrastra.
 */
export default function Articulos() {
  const { datos, cargando, error, recargar } = useApi(() => api.catalogoArticulos(), [], VIVO.normal);

  // Búsqueda y filtros sobreviven al F5 (ver `lib/persistencia.ts`). Los
  // marcados no: son la selección de una acción en curso, y revivirla sola
  // después de una recarga es pedir que alguien le cambie el proveedor a una
  // tanda que ya no está mirando.
  const [texto, setTexto] = useEstadoGuardado('articulos.busqueda', '');
  const [soloSinProveedor, setSoloSinProveedor] = useEstadoGuardado('articulos.sinProveedor', false);
  const [soloRevisar, setSoloRevisar] = useEstadoGuardado('articulos.revisar', false);
  const [soloSinMaximo, setSoloSinMaximo] = useEstadoGuardado('articulos.sinMaximo', false);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [enLote, setEnLote] = useState('');
  const [avisoTexto, setAvisoTexto] = useState<{ texto: string; ok: boolean } | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [confirmar, dialogo] = useConfirmar();
  // De dónde nacen los paneles que se abren desde un botón: en el celular el
  // botón queda al centro; en pantalla ancha, a la derecha.
  const celular = useEsCelular();

  // La ficha de renombrar/detalle: sólo una fila abierta a la vez. Se pide un
  // clic explícito para abrirla y otro para confirmar, a propósito: cambiar
  // la clave primaria de un material no es algo para disparar sin querer.
  const [abierto, setAbierto] = useState<string | null>(null);
  const [codigoNuevo, setCodigoNuevo] = useState('');
  const [nombreNuevo, setNombreNuevo] = useState('');

  // Carga de productos: bloque aparte, cerrado por default, para dar de alta
  // el maestro de materiales sin tocar stock ni ubicación.
  const [cargaAbierta, setCargaAbierta] = useState(false);
  const [modoCarga, setModoCarga] = useState<'archivo' | 'manual'>('archivo');
  const [filasCarga, setFilasCarga] = useState<FilaManual[]>(vaciarProductos);
  const [archivoCarga, setArchivoCarga] = useState<File | null>(null);
  const [previaCarga, setPreviaCarga] = useState<ImportacionProductos | null>(null);
  const [hechoCarga, setHechoCarga] = useState<ImportacionProductos | null>(null);
  const [errorCarga, setErrorCarga] = useState('');
  const [ocupadoCarga, setOcupadoCarga] = useState(false);
  const entradaCarga = useRef<HTMLInputElement>(null);

  const lista = useMemo(() => {
    const filtrados = (datos?.articulos ?? []).filter((a) => {
      if (soloSinProveedor && a.proveedor) return false;
      if (soloRevisar && pareceCodigoDeMaterial(a.codigo)) return false;
      if (soloSinMaximo && a.maxPorCapacho !== null) return false;
      return true;
    });
    return filtrar(filtrados, texto, (a) => [a.codigo, a.descripcion, a.proveedor]);
  }, [datos, texto, soloSinProveedor, soloRevisar, soloSinMaximo]);

  // El maestro tiene miles de renglones: se dibuja de a página.
  const pagina = usePaginado(lista);

  /** Devuelve si salió bien: quien cierra un formulario necesita saberlo. */
  async function accion(fn: () => Promise<unknown>, exito: string): Promise<boolean> {
    setGuardando(true);
    setAvisoTexto(null);
    try {
      await fn();
      setAvisoTexto({ texto: exito, ok: true });
      recargar();
      return true;
    } catch (err) {
      setAvisoTexto({ texto: mensajeDeError(err), ok: false });
      return false;
    } finally {
      setGuardando(false);
    }
  }

  /**
   * «Aplicar» de la carga de a varios. Vacío borra el proveedor de todos los
   * marcados, y con «Marcar todos» eso son cientos: pregunta siempre. Cargar
   * uno pregunta sólo pasando de 20, que ya no es una tanda que se revise a ojo.
   */
  async function aplicarEnLote() {
    const proveedor = enLote.trim();
    const n = marcados.size;
    const cuantos = `${n.toLocaleString('es-AR')} ${n === 1 ? 'artículo' : 'artículos'}`;
    if (!proveedor || n > 20) {
      const ok = await confirmar(
        proveedor
          ? {
              titulo: `¿Cargar «${proveedor}» en ${cuantos}?`,
              detalle: 'Reemplaza el proveedor que tengan cargado.',
              boton: `Cargar el proveedor en ${cuantos}`,
            }
          : {
              titulo: `¿Borrar el proveedor de ${cuantos}?`,
              detalle: 'El campo está vacío: los marcados quedan sin proveedor.',
              boton: `Borrar el proveedor de ${cuantos}`,
            },
      );
      if (!ok) return;
    }
    const ok = await accion(
      () => api.proveedorEnLote([...marcados], enLote),
      proveedor ? `Proveedor cargado en ${cuantos}.` : `Se borró el proveedor de ${cuantos}.`,
    );
    if (ok) {
      setMarcados(new Set());
      setEnLote('');
    }
  }

  function marcar(codigo: string) {
    setMarcados((antes) => {
      const ahora = new Set(antes);
      if (ahora.has(codigo)) ahora.delete(codigo);
      else ahora.add(codigo);
      return ahora;
    });
  }

  function alternarFicha(codigo: string) {
    if (abierto === codigo) {
      setAbierto(null);
      return;
    }
    setAbierto(codigo);
    setCodigoNuevo('');
    setNombreNuevo('');
  }

  function reiniciarCarga() {
    setArchivoCarga(null);
    setPreviaCarga(null);
    setHechoCarga(null);
    setErrorCarga('');
    if (entradaCarga.current) entradaCarga.current.value = '';
  }

  function cambiarModoCarga(nuevo: 'archivo' | 'manual') {
    reiniciarCarga();
    setModoCarga(nuevo);
  }

  function alternarCarga() {
    setCargaAbierta((v) => !v);
    reiniciarCarga();
    setModoCarga('archivo');
    setFilasCarga(vaciarProductos());
  }

  /** Tocar una celda invalida la previa: lo que se vio ya no es lo que se cargaría. */
  function editarCarga(indice: number, clave: string, valor: string) {
    setFilasCarga((prev) => prev.map((f, i) => (i === indice ? { ...f, [clave]: valor } : f)));
    setArchivoCarga(null);
    setPreviaCarga(null);
    setErrorCarga('');
  }

  async function analizarCarga(f: File) {
    setOcupadoCarga(true);
    setErrorCarga('');
    setPreviaCarga(null);
    setHechoCarga(null);
    setArchivoCarga(f);
    try {
      setPreviaCarga(await api.importarProductos(f, false));
    } catch (err) {
      setErrorCarga(mensajeDeError(err));
      setArchivoCarga(null);
    } finally {
      setOcupadoCarga(false);
    }
  }

  async function confirmarCarga() {
    if (!archivoCarga) return;
    setOcupadoCarga(true);
    setErrorCarga('');
    try {
      setHechoCarga(await api.importarProductos(archivoCarga, true));
      setPreviaCarga(null);
      setFilasCarga(vaciarProductos());
      recargar();
    } catch (err) {
      setErrorCarga(mensajeDeError(err));
    } finally {
      setOcupadoCarga(false);
    }
  }

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (cargando && !datos) return <Cargando texto="Buscando los artículos…" />;
  if (!datos) return null;

  const todosMarcados = lista.length > 0 && lista.every((a) => marcados.has(a.codigo));

  const resumenCarga = hechoCarga?.resumen ?? previaCarga?.resumen;
  const problemasCarga = hechoCarga?.problemas ?? previaCarga?.problemas ?? [];
  const cargadasCarga = filasCarga.filter(tieneAlgo).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Encabezado
        bajada="El catálogo se llena solo al importar inventario. Acá se completa lo que la planilla no trae, como el proveedor, y se corrige un código de material mal cargado."
      >
        <button type="button" onClick={alternarCarga} aria-expanded={cargaAbierta} style={botonPrimario}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={cargaAbierta ? 'cierra' : 'abre'}
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.16, ease: CURVA }}
              style={{ display: 'flex' }}
            >
              <Icono nombre={cargaAbierta ? 'expand_less' : 'add'} size={19} />
            </motion.span>
          </AnimatePresence>
          Cargar productos
        </button>
      </Encabezado>

      <AnimatePresence initial={false}>
      {cargaAbierta && (
        <motion.div key="carga" {...desplegar}>
        {/* Nace en el botón «Cargar productos» (arriba a la derecha) y se abre en círculo. */}
        <motion.div {...revelarDesde(celular ? '50%' : 'calc(100% - 90px)', '-30px')}>
        <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className="min-w-full tablet:min-w-0" style={{ fontSize: 15, fontWeight: 600, color: c.texto, flex: 1 }}>Cargar productos</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <ChipBoton grupo="articulos-modo-carga" activo={modoCarga === 'archivo'} onClick={() => cambiarModoCarga('archivo')}>
                Desde un archivo
              </ChipBoton>
              <ChipBoton grupo="articulos-modo-carga" activo={modoCarga === 'manual'} onClick={() => cambiarModoCarga('manual')}>
                A mano
              </ChipBoton>
            </div>
            {modoCarga === 'archivo' && (
              <>
                <button
                  type="button"
                  onClick={() => void api.descargarPlantillaProductos()}
                  className="basis-full tablet:basis-auto"
                  style={botonSecundario}
                >
                  <Icono nombre="download" size={19} />
                  Descargar plantilla
                </button>
                <label className="basis-full tablet:basis-auto" style={{ ...botonPrimario, position: 'relative', minHeight: 48 }}>
                  <Icono nombre="upload_file" size={19} />
                  {archivoCarga ? 'Elegir otro archivo' : 'Elegir archivo'}
                  <input
                    ref={entradaCarga}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void analizarCarga(f);
                    }}
                    style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
                  />
                </label>
              </>
            )}
          </div>

          <div style={nota}>
            Esto no carga stock: sólo da de alta qué productos existen, para que un ingreso no tenga que crearlos
            sobre la marcha.
          </div>

          {errorCarga && <PanelError mensaje={errorCarga} />}

          {/* Cada bloque entra y sale animado: lo que se muestra depende de en qué paso va la carga. */}
          <AnimatePresence initial={false}>
          {modoCarga === 'archivo' && !previaCarga && !hechoCarga && !errorCarga && (
            <motion.div key="instrucciones" {...aparecer} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 13.5, color: c.texto2, textWrap: 'pretty' }}>
                Un CSV con una fila por material. Desde Excel: <em>Archivo → Guardar como → CSV UTF-8</em>. Los
                encabezados pueden estar en cualquier orden.
              </div>
              <div style={{ ...nota, fontFamily: mono, fontSize: 12, whiteSpace: 'pre-wrap', color: c.texto }}>
                {'codigo;nombre;detalle;proveedor;max_por_capacho\n'}
                {'1721423040;TUBE;OUTER TUBE 65x82x389;ZF Friedrichshafen AG;45'}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: c.texto2, display: 'grid', gap: 5 }}>
                <li>
                  <strong>Obligatorio:</strong> el código. Acá no va cantidad, pallet ni ubicación.
                </li>
                <li>
                  Un código que ya está en el catálogo se actualiza; uno nuevo se crea. Si no viene el nombre, el
                  material queda llamándose como su código hasta que alguien se lo ponga.
                </li>
              </ul>
            </motion.div>
          )}

          {modoCarga === 'manual' && !hechoCarga && (
            <motion.div key="manual" {...abrir(16)} style={{ ...tarjeta, overflow: 'hidden' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '14px 16px',
                  borderBottom: `1px solid ${c.borde}`,
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 600, color: c.texto, flex: 1 }}>Cargar a mano</span>
                <span style={{ fontSize: 12.5, color: c.texto3 }}>un material por fila</span>
              </div>
              <div className="wms-scroll-x">
                <div style={{ minWidth: 'max-content' }}>
                  <div style={encabezadoTabla}>
                    {COLUMNAS_PRODUCTOS.map((col) => (
                      <span key={col.clave} style={{ width: col.ancho, flex: 'none' }}>
                        {col.rotulo}
                      </span>
                    ))}
                    <span style={{ width: 44, flex: 'none' }} />
                  </div>
                  {filasCarga.map((fila, i) => (
                    <motion.div key={i} {...filaNueva(i)} style={filaTabla}>
                      {COLUMNAS_PRODUCTOS.map((col) => (
                        <input
                          key={col.clave}
                          value={fila[col.clave] ?? ''}
                          placeholder={col.ejemplo}
                          onChange={(e) => editarCarga(i, col.clave, e.target.value)}
                          style={{ ...campoTabla, width: col.ancho, flex: 'none' }}
                        />
                      ))}
                      <button
                        type="button"
                        title="Quitar la fila"
                        onClick={() =>
                          setFilasCarga((prev) =>
                            prev.length > 1 ? prev.filter((_, j) => j !== i) : vaciarProductos(),
                          )
                        }
                        style={{ ...botonSecundario, width: 44, minHeight: altoMinimo(36), padding: 0, borderRadius: 999 }}
                      >
                        <Icono nombre="close" size={17} />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setFilasCarga((prev) => [...prev, filaVacia(COLUMNAS_PRODUCTOS)])}
                  style={{ ...botonSecundario, padding: '9px 16px' }}
                >
                  <Icono nombre="add" size={18} />
                  Agregar fila
                </button>
                <span style={{ flex: 1, minWidth: 120, fontSize: 13, color: c.texto3 }}>
                  {cargadasCarga === 0 ? (
                    'Ninguna fila cargada todavía.'
                  ) : (
                    <>
                      <Numero valor={cargadasCarga} /> filas cargadas.
                    </>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    void analizarCarga(csvDeFilas(COLUMNAS_PRODUCTOS, filasCarga, 'productos-manual.csv'))
                  }
                  disabled={ocupadoCarga || cargadasCarga === 0}
                  style={{ ...botonPrimario, opacity: ocupadoCarga || cargadasCarga === 0 ? 0.5 : 1 }}
                >
                  <Cambia clave={ocupadoCarga ? 'revisando' : 'previsualizar'}>
                    {ocupadoCarga ? 'Revisando…' : 'Previsualizar'}
                  </Cambia>
                </button>
              </div>
            </motion.div>
          )}

          {ocupadoCarga && !resumenCarga && (
            <motion.div
              key="ocupado"
              {...aparecer}
              style={{ ...tarjeta, padding: 20, fontSize: 13.5, color: c.texto2 }}
              className="wms-cargando"
            >
              {modoCarga === 'manual' ? 'Revisando las filas…' : 'Leyendo el archivo…'}
            </motion.div>
          )}

          {hechoCarga && (
            <motion.div key="hecho" {...abrir(16)} role="status" style={{ ...aviso('ok'), display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <Icono nombre="task_alt" size={24} color={c.verde} />
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Productos cargados</div>
                <div style={{ marginTop: 2, fontSize: 13, color: c.texto2 }}>
                  {miles(hechoCarga.resumen.nuevos)} nuevos, {miles(hechoCarga.resumen.actualizados)} actualizados.
                </div>
              </div>
              <button type="button" onClick={reiniciarCarga} style={{ ...botonSecundario, padding: '9px 16px' }}>
                Cargar más
              </button>
            </motion.div>
          )}

          {resumenCarga && (
            <motion.div key="resumen" {...aparecer} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 16 }}>
              <Kpi rotulo="Nuevos" valor={resumenCarga.nuevos} pie="se crean en el catálogo" color={c.verde} />
              <Kpi rotulo="Se actualizan" valor={resumenCarga.actualizados} pie="ya estaban" />
              <Kpi
                rotulo="Con problemas"
                valor={resumenCarga.conProblemas}
                pie={resumenCarga.conProblemas ? 'no se cargan' : 'ninguna'}
                color={resumenCarga.conProblemas ? c.rojo : c.texto}
              />
            </motion.div>
          )}

          {resumenCarga && resumenCarga.sinNombre > 0 && (
            <motion.div key="sin-nombre" {...abrir(16)} style={aviso('warn')}>
              {resumenCarga.sinNombre === 1
                ? 'Hay 1 material sin nombre en la planilla.'
                : `Hay ${miles(resumenCarga.sinNombre)} materiales sin nombre en la planilla.`}{' '}
              Van a quedar llamándose como su código hasta que alguien les ponga el nombre.
            </motion.div>
          )}

          {previaCarga && (
            <motion.div key="confirmar" {...abrir(16)} style={{ ...tarjeta, padding: 20, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240, fontSize: 13.5, color: c.texto2, textWrap: 'pretty' }}>
                {previaCarga.resumen.validos > 0
                  ? 'Todavía no se escribió nada. Revisá el resumen y confirmá para cargarlo.'
                  : 'Ninguna fila se puede cargar. Corregí lo que está marcado abajo y volvé a intentar.'}
              </div>
              <button type="button" onClick={reiniciarCarga} style={botonSecundario}>
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarCarga}
                disabled={ocupadoCarga || previaCarga.resumen.validos === 0}
                style={{ ...botonPrimario, opacity: ocupadoCarga || previaCarga.resumen.validos === 0 ? 0.5 : 1 }}
              >
                <Cambia clave={ocupadoCarga ? 'cargando' : 'confirmar'}>
                  {ocupadoCarga ? 'Cargando…' : `Confirmar ${miles(previaCarga.resumen.validos)} materiales`}
                </Cambia>
              </button>
            </motion.div>
          )}

          {problemasCarga.length > 0 && (
            <motion.div key="problemas" {...abrir(16)} style={{ ...tarjeta, overflow: 'hidden' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '14px 16px',
                  borderBottom: `1px solid ${c.borde}`,
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 600, color: c.texto, flex: 1 }}>Filas con problemas</span>
                <EstadoChip estado={`${problemasCarga.length} sin cargar`} tono="err" />
              </div>
              <div className="wms-scroll-x">
                <div>
                  <div style={encabezadoTabla}>
                    <span style={{ width: 70 }}>Línea</span>
                    <span style={{ width: 150 }}>Código</span>
                    <span style={{ flex: 1, minWidth: 220 }}>Motivo</span>
                  </div>
                  {problemasCarga.map((p) => (
                    <div key={`${p.linea}-${p.codigo}`} style={{ ...filaTabla, background: c.rojoFila }}>
                      <span style={{ width: 70, fontVariantNumeric: 'tabular-nums', color: c.texto2 }}>
                        {p.linea}
                      </span>
                      <span style={{ width: 150, fontFamily: mono, fontSize: 13 }}>{p.codigo}</span>
                      <span style={{ flex: 1, minWidth: 220, color: c.texto2 }}>{p.motivo}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {previaCarga && previaCarga.muestra && previaCarga.muestra.length > 0 && (
            <motion.div key="muestra" {...abrir(16)} style={{ ...tarjeta, overflow: 'hidden' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '14px 16px',
                  borderBottom: `1px solid ${c.borde}`,
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 600, color: c.texto, flex: 1 }}>Cómo va a quedar</span>
                <span style={{ fontSize: 12.5, color: c.texto3 }}>primeras {previaCarga.muestra.length} filas</span>
              </div>
              <div className="wms-scroll-x">
                <div>
                  <div style={encabezadoTabla}>
                    <span style={{ width: 150 }}>Código</span>
                    <span style={{ flex: 1, minWidth: 180 }}>Nombre</span>
                    <span style={{ flex: 1, minWidth: 180 }}>Proveedor</span>
                    <span style={{ width: 120 }}>Pz. por capacho</span>
                    <span style={{ width: 90 }}>Catálogo</span>
                  </div>
                  {previaCarga.muestra.map((m, i) => (
                    <motion.div key={`${m.codigo}-${i}`} {...filaNueva(i)} style={filaTabla}>
                      <span style={{ width: 150, fontFamily: mono, fontSize: 13 }}>{m.codigo}</span>
                      <span style={{ flex: 1, minWidth: 180, color: c.texto2 }}>{m.nombre}</span>
                      <span style={{ flex: 1, minWidth: 180, color: c.texto2 }}>{m.proveedor ?? '—'}</span>
                      <span style={{ width: 120, color: c.texto2, fontVariantNumeric: 'tabular-nums' }}>
                        {m.maxPorCapacho ?? '—'}
                      </span>
                      <span style={{ width: 90 }}>
                        {m.nuevo ? <EstadoChip estado="nuevo" tono="warn" /> : <EstadoChip estado="ya está" tono="ok" />}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
          </AnimatePresence>
        </div>
        </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      <AnimatePresence>
        {avisoTexto && (
          <motion.div key={avisoTexto.texto} {...aparecer} role={avisoTexto.ok ? 'status' : 'alert'} style={aviso(avisoTexto.ok ? 'ok' : 'err')}>
            {avisoTexto.texto}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {datos.sinProveedor > 0 && (
          <motion.div key="sin-proveedor" {...abrir(16)} style={aviso('warn')}>
            {datos.sinProveedor === 1 ? (
              'Hay 1 artículo sin proveedor cargado.'
            ) : (
              <>
                Hay <Numero valor={datos.sinProveedor} /> artículos sin proveedor cargado.
              </>
            )}{' '}
            En la etiqueta ese dato va a salir vacío.
          </motion.div>
        )}

        {datos.sinMaximo > 0 && (
          <motion.div key="sin-maximo" {...abrir(16)} style={aviso('warn')}>
            {datos.sinMaximo === 1 ? (
              'Hay 1 artículo sin las piezas por capacho cargadas.'
            ) : (
              <>
                Hay <Numero valor={datos.sinMaximo} /> artículos sin las piezas por capacho cargadas.
              </>
            )}{' '}
            Las órdenes de trabajo de esos materiales entran en un solo capacho con todo lo pedido, y hay que
            repartirlas a mano en el depósito.
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col gap-3 tablet:flex-row tablet:flex-wrap tablet:items-center">
        {/* En columna el `flex: 1` del buscador le sacaría todo el alto: se lo
            envuelve para que reparta ancho; de tablet para arriba el envoltorio
            desaparece y el buscador queda como siempre. */}
        <div className="flex tablet:contents">
          <Buscador valor={texto} onCambiar={setTexto} placeholder="Código, nombre o proveedor" />
        </div>

        {/* En el celular los filtros corren de costado en una sola tira. */}
        <div className="wms-tira" style={{ gap: 12, alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => setSoloSinMaximo((v) => !v)}
          aria-pressed={soloSinMaximo}
          title="Materiales sin las piezas por capacho: sus órdenes de trabajo entran sin repartir."
          style={{
            ...botonSecundario,
            minHeight: altoMinimo(40),
            background: soloSinMaximo ? c.ambarSuave : c.panel,
            borderColor: soloSinMaximo ? c.ambar : c.bordeFuerte,
            color: soloSinMaximo ? c.ambar : c.texto2,
          }}
        >
          Sin piezas por capacho
        </button>

        <button
          type="button"
          onClick={() => setSoloSinProveedor((v) => !v)}
          aria-pressed={soloSinProveedor}
          style={{
            ...botonSecundario,
            minHeight: altoMinimo(40),
            background: soloSinProveedor ? c.verdeSuave : c.panel,
            borderColor: soloSinProveedor ? c.verde : c.bordeFuerte,
            color: soloSinProveedor ? c.verde : c.texto2,
          }}
        >
          Sin proveedor
        </button>

        <button
          type="button"
          onClick={() => setSoloRevisar((v) => !v)}
          aria-pressed={soloRevisar}
          title="Materiales cuyo código no tiene ningún dígito: pueden tener el nombre y el código cargados al revés."
          style={{
            ...botonSecundario,
            minHeight: altoMinimo(40),
            background: soloRevisar ? c.ambarSuave : c.panel,
            borderColor: soloRevisar ? c.ambar : c.bordeFuerte,
            color: soloRevisar ? c.ambar : c.texto2,
          }}
        >
          A revisar
        </button>

        <span style={{ fontSize: 12.5, color: c.texto3 }}>
          <Numero valor={lista.length} /> de <Numero valor={datos.articulos.length} />
        </span>
        </div>
      </div>

      {/* -------------------------------------------- carga de a varios */}
      <AnimatePresence>
      {marcados.size > 0 && (
        <motion.div key="lote" {...desplegar}>
        {/* Nace desde las casillas de la tabla, que están debajo y a la izquierda. */}
        <motion.div {...revelarDesde('30px', 'calc(100% + 24px)')}>
        <div style={{ ...tarjeta, padding: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="basis-full tablet:basis-[220px]" style={{ ...etiquetaCampo, flexGrow: 1, flexShrink: 1 }}>
            Proveedor para{' '}
            {marcados.size === 1 ? (
              'el artículo marcado'
            ) : (
              <>
                los <Numero valor={marcados.size} /> artículos marcados
              </>
            )}
            <input
              value={enLote}
              onChange={(e) => setEnLote(e.target.value)}
              list="wms-proveedores"
              placeholder="ZF Friedrichshafen AG"
              style={campo}
            />
          </label>
          <button
            type="button"
            onClick={() => void aplicarEnLote()}
            disabled={guardando}
            className="flex-1 tablet:flex-none"
            style={{ ...botonPrimario, minHeight: altoMinimo(42), opacity: guardando ? 0.7 : 1 }}
          >
            Aplicar
          </button>
          <button
            type="button"
            onClick={() => setMarcados(new Set())}
            className="flex-1 tablet:flex-none"
            style={{ ...botonSecundario, minHeight: altoMinimo(42) }}
          >
            Desmarcar
          </button>
        </div>
        </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      <datalist id="wms-proveedores">
        {datos.proveedores.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>

      <div style={{ ...tarjeta, overflow: 'hidden' }}>
        <div className="wms-scroll-x">
          <table className="wms-tarjetas" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: c.fondo, borderBottom: `1px solid ${c.borde}` }}>
                <th style={{ ...titulo, minWidth: 220, whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label style={tildeEnFila}>
                      <input
                        type="checkbox"
                        checked={todosMarcados}
                        onChange={() => setMarcados(todosMarcados ? new Set() : new Set(lista.map((a) => a.codigo)))}
                        aria-label="Marcar todos"
                      />
                    </label>
                    Código de material
                  </span>
                </th>
                <th style={{ ...titulo, minWidth: 180, width: '28%' }}>Nombre del material</th>
                <th style={{ ...titulo, minWidth: 200, width: '28%' }}>Proveedor</th>
                <th
                  style={{ ...titulo, width: 150 }}
                  title="Cuántas piezas entran en un capacho: con eso se parte la orden de trabajo."
                >
                  Piezas por capacho
                </th>
                <th style={{ ...titulo, width: 110, textAlign: 'right' }}>En depósito</th>
                <th style={{ ...titulo, width: 62 }} />
              </tr>
            </thead>
            <tbody>

            {pagina.visibles.map((a, n) => {
              const revisar = !pareceCodigoDeMaterial(a.codigo);
              const abiertaEstaFicha = abierto === a.codigo;
              const nuevoLimpio = codigoNuevo.trim();
              const puedeRenombrar = nuevoLimpio !== '' && nuevoLimpio !== a.codigo;

              return (
                <Fragment key={a.codigo}>
                  <motion.tr
                    {...fila(n)}
                    // Reacomoda sola al filtrar; la casilla marcada tiñe la fila en vez de cambiarla de golpe.
                    layout="position"
                    initial={{ ...fila(n).initial, backgroundColor: 'rgba(0,0,0,0)' }}
                    animate={{ ...fila(n).animate, backgroundColor: marcados.has(a.codigo) ? c.verdeSuave : 'rgba(0,0,0,0)' }}
                    transition={{ ...fila(n).transition, backgroundColor: { duration: 0.25, ease: CURVA } }}
                  >
                    <td className="wms-principal" style={celda}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <label style={tildeEnFila}>
                        <input
                          type="checkbox"
                          checked={marcados.has(a.codigo)}
                          onChange={() => marcar(a.codigo)}
                          aria-label={`Marcar ${a.codigo}`}
                        />
                      </label>
                      <span style={{ fontFamily: mono, fontSize: 12.5, color: c.texto }}>{a.codigo}</span>
                      {revisar && (
                        <span title="El código no tiene ningún dígito: puede que el nombre y el código estén cargados al revés.">
                          <EstadoChip estado="revisar" tono="warn" />
                        </span>
                      )}
                    </span>
                    </td>
                    <td data-rotulo="Nombre" style={celda}>
                      {/* El nombre se corrige acá. Antes sólo se podía cambiar
                          de paso al renombrar el código, que es otra cosa: un
                          nombre mal escrito no obliga a tocar la clave. */}
                      <input
                        defaultValue={a.descripcion}
                        placeholder={a.codigo}
                        aria-label={`Nombre de ${a.codigo}`}
                        onBlur={(e) => {
                          const valor = e.target.value.trim();
                          if (valor === a.descripcion || valor === '') {
                            e.target.value = a.descripcion;
                            return;
                          }
                          void accion(
                            () => api.guardarArticulo(a.codigo, { descripcion: valor }),
                            `${a.codigo}: ahora se llama ${valor}.`,
                          );
                        }}
                        style={{ ...campoEnFila, border: `1px solid ${c.borde}` }}
                      />
                    </td>
                    <td data-rotulo="Proveedor" style={celda}>
                      {/* Se edita en la fila: entrar a una ficha por artículo para
                          cargar un solo dato sería dar vueltas de más. */}
                      <input
                        defaultValue={a.proveedor}
                        list="wms-proveedores"
                        placeholder="Sin cargar"
                        aria-label={`Proveedor de ${a.codigo}`}
                        onBlur={(e) => {
                          const valor = e.target.value.trim();
                          if (valor === a.proveedor) return;
                          void accion(
                            () => api.guardarArticulo(a.codigo, { proveedor: valor }),
                            `${a.codigo}: proveedor ${valor ? `cargado` : 'borrado'}.`,
                          );
                        }}
                        style={{
                          ...campoEnFila,
                          border: `1px solid ${a.proveedor ? c.borde : c.bordeFuerte}`,
                          background: a.proveedor ? c.panel : c.fondo,
                        }}
                      />
                    </td>
                    <td data-rotulo="Piezas por capacho" style={celda}>
                      {/* Vacío es «no se cargó», y entonces la orden de trabajo
                          entra sin repartir. Por eso se marca distinto en vez
                          de mostrar un cero, que no querría decir nada. */}
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        defaultValue={a.maxPorCapacho ?? ''}
                        placeholder="Sin cargar"
                        aria-label={`Piezas por capacho de ${a.codigo}`}
                        onBlur={(e) => {
                          const texto = e.target.value.trim();
                          const valor = texto === '' ? null : Math.trunc(Number(texto));
                          if (valor === (a.maxPorCapacho ?? null)) return;
                          if (valor !== null && (!Number.isFinite(valor) || valor < 1)) {
                            e.target.value = a.maxPorCapacho === null ? '' : String(a.maxPorCapacho);
                            return;
                          }
                          void accion(
                            () => api.guardarArticulo(a.codigo, { maxPorCapacho: valor }),
                            valor === null
                              ? `${a.codigo}: sus órdenes vuelven a entrar sin repartir.`
                              : `${a.codigo}: ${valor} piezas por capacho.`,
                          );
                        }}
                        style={{
                          ...campoEnFila,
                          border: `1px solid ${a.maxPorCapacho === null ? c.bordeFuerte : c.borde}`,
                          background: a.maxPorCapacho === null ? c.fondo : c.panel,
                        }}
                      />
                    </td>
                    <td
                      data-rotulo="En depósito"
                      style={{
                        ...celda,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        fontSize: 12.5,
                        color: c.texto3,
                      }}
                    >
                      {a.pallets} {a.pallets === 1 ? 'pallet' : 'pallets'}
                    </td>
                    <td className="wms-acciones" style={{ ...celda, textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => alternarFicha(a.codigo)}
                        aria-expanded={abiertaEstaFicha}
                        aria-label={`Más datos de ${a.codigo}`}
                        title="Descripción larga y corrección del código"
                        style={{
                          width: 30,
                          height: 30,
                          // En táctil el botón llega a 44, como el resto.
                          minWidth: 'var(--toque, 30px)',
                          minHeight: 'var(--toque, 30px)',
                          borderRadius: 8,
                          border: `1px solid ${c.borde}`,
                          background: abiertaEstaFicha ? c.fondo : c.panel,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <motion.span animate={{ rotate: abiertaEstaFicha ? 180 : 0 }} transition={{ duration: 0.3, ease: CURVA }} style={{ display: 'flex' }}>
                          <Icono nombre="expand_more" size={18} color={c.texto3} />
                        </motion.span>
                      </button>
                    </td>
                  </motion.tr>

                  <AnimatePresence initial={false}>
                  {abiertaEstaFicha && (
                    <motion.tr
                      key="ficha"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, transition: { duration: 0.24, ease: CURVA } }}
                      transition={{ duration: 0.2 }}
                      style={{ background: c.fondo }}
                    >
                    <td colSpan={6} className="wms-principal" style={{ ...celda, padding: 0 }}>
                    {/* Crece hacia abajo y nace en círculo desde el botón de la fila de arriba. */}
                    <motion.div {...desplegar}>
                    <motion.div {...revelarDesde(celular ? '50%' : 'calc(100% - 31px)', '0px')}>
                    <div
                      className="pt-4 pr-4 pb-5 pl-4 tablet:pl-[66px]"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <label style={{ ...etiquetaCampo, maxWidth: 480 }}>
                        Descripción larga
                        <input
                          defaultValue={a.detalle ?? ''}
                          placeholder="Descripción de la planilla del cliente"
                          aria-label={`Descripción larga de ${a.codigo}`}
                          onBlur={(e) => {
                            const valor = e.target.value.trim();
                            if (valor === (a.detalle ?? '')) return;
                            void accion(
                              () => api.guardarArticulo(a.codigo, { detalle: valor }),
                              `${a.codigo}: descripción larga actualizada.`,
                            );
                          }}
                          style={campo}
                        />
                        <span style={{ fontSize: 12, color: c.texto3, fontWeight: 400 }}>
                          Se guarda pero no se muestra en la etiqueta ni en los combos.
                        </span>
                      </label>

                      <div
                        style={{
                          paddingTop: 14,
                          borderTop: `1px dashed ${c.bordeFuerte}`,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 10,
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 600, color: c.texto }}>
                          Corregir el código de material
                        </span>

                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                          <label style={{ ...etiquetaCampo, flex: 1, minWidth: 180 }}>
                            Código nuevo
                            <input
                              value={codigoNuevo}
                              onChange={(e) => setCodigoNuevo(e.target.value)}
                              placeholder={a.codigo}
                              aria-label={`Código nuevo para ${a.codigo}`}
                              autoComplete="off"
                              spellCheck={false}
                              style={{ ...campo, fontFamily: mono }}
                            />
                          </label>
                          <label style={{ ...etiquetaCampo, flex: 1, minWidth: 180 }}>
                            Nombre del material (opcional)
                            <input
                              value={nombreNuevo}
                              onChange={(e) => setNombreNuevo(e.target.value)}
                              placeholder={a.descripcion}
                              aria-label={`Nombre nuevo para ${a.codigo}`}
                              style={campo}
                            />
                          </label>
                        </div>

                        <div style={{ fontSize: 12.5, color: c.texto2, textWrap: 'pretty' }}>
                          {a.pallets} {a.pallets === 1 ? 'pallet' : 'pallets'} y {a.piezas}{' '}
                          {a.piezas === 1 ? 'pieza' : 'piezas'} siguen apuntando a este material y se actualizan
                          solos al confirmar.
                        </div>

                        <div style={{ display: 'flex', gap: 10 }}>
                          <button
                            type="button"
                            disabled={!puedeRenombrar || guardando}
                            onClick={() =>
                              accion(
                                () =>
                                  api.renombrarArticulo(a.codigo, {
                                    codigo: nuevoLimpio,
                                    descripcion: nombreNuevo.trim() || undefined,
                                  }),
                                `${a.codigo} ahora es ${nuevoLimpio}.`,
                                // Sólo se cierra si salió bien. El caso que
                                // importa es el 409 —ese código ya existe—: si
                                // la ficha se cerrara, lo tipeado se pierde y
                                // hay que volver a empezar para cambiar un
                                // dígito.
                              ).then((ok) => ok && setAbierto(null))
                            }
                            style={{
                              ...botonPrimario,
                              minHeight: altoMinimo(40),
                              opacity: puedeRenombrar && !guardando ? 1 : 0.55,
                            }}
                          >
                            Confirmar renombre
                          </button>
                          <button
                            type="button"
                            onClick={() => setAbierto(null)}
                            style={{ ...botonSecundario, minHeight: altoMinimo(40) }}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </div>
                    </motion.div>
                    </motion.div>
                    </td>
                    </motion.tr>
                  )}
                  </AnimatePresence>
                </Fragment>
              );
            })}
            </tbody>
          </table>
        </div>

        <Paginador
          pagina={pagina.pagina}
          paginas={pagina.paginas}
          total={pagina.total}
          desde={pagina.desde}
          que="artículos"
          onIr={pagina.setPagina}
        />

        {lista.length === 0 && (
          <Vacio
            icono={datos.articulos.length === 0 ? 'inventory_2' : 'search_off'}
            titulo={datos.articulos.length === 0 ? 'Todavía no hay artículos' : 'Sin resultados'}
            detalle={
              datos.articulos.length === 0
                ? 'Se cargan solos al importar el inventario o al recibir un contenedor.'
                : 'No encontramos artículos que coincidan con la búsqueda.'
            }
          />
        )}
      </div>

      <div style={nota}>
        El importador también trae el proveedor si la planilla tiene una columna «proveedor». Lo que se carga acá no se
        pisa al volver a importar: sólo se completa lo que venga con dato. El chip «revisar» es una pista, no un
        diagnóstico seguro: marca los códigos sin ningún dígito, que suelen ser nombres cargados en la columna
        equivocada.
      </div>
      {dialogo}
    </div>
  );
}
